/**
 * Created: 2026-10-29
 * Initial commit: Port event start / score entry / completion from mini to web admin
 *
 * Dialog for entering (or deleting) one set's score of an event match.
 */

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'

const MAX_SCORE: Record<'TENNIS' | 'PICKLEBALL', number> = {
  TENNIS: 10,
  PICKLEBALL: 21,
}

export interface SetScore {
  side1Score: number
  side2Score: number
}

interface SetScoreDialogProps {
  setNumber: number
  initialScore?: SetScore
  sport: 'TENNIS' | 'PICKLEBALL'
  side1Label: string
  side2Label: string
  onConfirm: (score: SetScore) => void
  onDelete?: () => void
  onClose: () => void
}

export default function SetScoreDialog({
  setNumber,
  initialScore,
  sport,
  side1Label,
  side2Label,
  onConfirm,
  onDelete,
  onClose,
}: SetScoreDialogProps) {
  const [score, setScore] = useState<SetScore>(
    initialScore ?? { side1Score: 0, side2Score: 0 },
  )
  const options = Array.from({ length: MAX_SCORE[sport] + 1 }, (_, i) => i)
  const error = score.side1Score === score.side2Score ? '局分不能相等' : null

  const scoreSelect = (label: string, field: keyof SetScore) => (
    <TextField
      select
      size="small"
      label={label}
      value={score[field]}
      onChange={(e) => setScore({ ...score, [field]: Number(e.target.value) })}
      sx={{ flex: 1 }}
    >
      {options.map((n) => (
        <MenuItem key={n} value={n}>
          {n}
        </MenuItem>
      ))}
    </TextField>
  )

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>第 {setNumber} 盘比分</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 1 }}>
          {scoreSelect(side1Label, 'side1Score')}
          <Typography>:</Typography>
          {scoreSelect(side2Label, 'side2Score')}
        </Box>
        <Typography
          variant="caption"
          sx={{ color: 'error.main', display: 'block', minHeight: 20, mt: 1 }}
        >
          {error}
        </Typography>
      </DialogContent>
      <DialogActions>
        {onDelete && (
          <Button color="error" onClick={onDelete} sx={{ mr: 'auto' }}>
            删除本盘
          </Button>
        )}
        <Button onClick={onClose}>取消</Button>
        <Button variant="contained" disabled={!!error} onClick={() => onConfirm(score)}>
          确定
        </Button>
      </DialogActions>
    </Dialog>
  )
}
